import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, inject, ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MFDTransactions, ReportService } from '@core/services/report.service';
import { ButtonDirective } from '@shared/directives/button.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountManagementService } from '@core/services/account-management.service';
import { PermissionDirective } from '@shared/directives/permission.directive';
@Component({
  selector: 'app-export-reports',
  standalone: true,
  imports: [
    SlideInRightModalComponent,
    InputComponent,
    InputLabelComponent,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    ButtonDirective,
    PermissionDirective,
  ],
  templateUrl: './export-reports.component.html',
  styleUrl: './export-reports.component.scss',
})
export class ExportReportsComponent {
  reportService = inject(ReportService);
  router = inject(Router);
  subs: Subscription[] = [];
  fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private change = inject(ChangeDetectorRef);
  private accountService = inject(AccountManagementService);

  export_format: string = this.activatedRoute.snapshot.params['export_format'];
  isLoading: boolean = false;
  returnPathUrl: string = '';
  reportUrl!: string;
  reportTitle: string = 'Report';
  reportName!: string;
  showDateFields: boolean = false;
  accountNumber!: string;
  RMId!: string;
  accountId: string = '';
  filterParams: any;

  dateRangeForm = this.fb.nonNullable.group({
    end_date: [''],
    start_date: [''],
  });

  ngOnInit(): void {
    const currentUrl = window.location.href;
    const basePath = currentUrl.split('/export-report')[0];
    this.reportName = basePath.split('/').pop() || '';

    this.accountNumber =
      this.activatedRoute.snapshot.parent?.params['accountNumber'] ||
      this.activatedRoute.snapshot?.params['account_number'] ||
      '';
    this.RMId =
      this.activatedRoute.snapshot.queryParams['rmid'] ||
      this.activatedRoute.snapshot?.params['rmid'] ||
      '';
    const { page, ...filterParams } = this.activatedRoute.snapshot.queryParams;
    this.filterParams = filterParams;

    this.showDateFields =
      this.reportName === 'funded-clients-report' ||
      this.reportName === 'subred' ||
      this.reportName === 'mutual-funds' ||
      this.reportName === 'dividend' ||
      this.reportName === 'mutual-funds' ||
      this.reportName === 'consolidated';
    this.change.detectChanges();
  }

  getReport() {
    switch (this.reportName) {
      case 'active-clients-report':
        this.getActiveAndInactiveClientReport('active', this.filterParams);
        break;

      case 'inactive-clients-report':
        this.getActiveAndInactiveClientReport('inactive', this.filterParams);
        break;

      case 'funded-clients-report':
        this.getCientWithFundsReport(this.filterParams);
        break;

      case 'portfolio-balance-report':
        this.getGlobalPortfolioReport(this.filterParams);
        break;

      case 'general-report':
        this.getClientDirectoryReport(this.filterParams);
        break;

      case 'subred':
      case 'mutual-funds':
        this.getSubRedReport();
        break;
      case 'dividend':
      case 'dividends-history':
        this.getDividendReport();
        break;
      case 'consolidated':
        this.getConsolidatedReport();
        break;
      case 'accountrm':
        this.getAccountRMReport();
        break;

      default:
        console.warn(`No report handler found for: ${this.reportName}`);
        break;
    }
  }

  getClientDirectoryReport(filterParams: any) {
    this.isLoading = true;
    const sub = this.reportService
      .getClientDirectoryReport(this.dateRangeForm.getRawValue(), filterParams)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.reportUrl = response.data.statement;
          this.change.detectChanges();
        },
        error: (resp) => {},
      });
    this.subs.push(sub);
  }

  getConsolidatedReport() {
    this.isLoading = true;
    const sub = this.reportService
      .getConsolidatedReport(this.dateRangeForm.getRawValue())
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.reportUrl = response.data.presigned_url;
          this.change.detectChanges();
        },
        error: (resp) => {},
      });
    this.subs.push(sub);
  }

  getCientWithFundsReport(filterParams: any) {
    this.isLoading = true;
    const sub = this.reportService
      .getCientWithFundsReport(
        this.dateRangeForm.getRawValue(),
        this.export_format,
        filterParams
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.reportUrl = response.data.statement;
          this.change.detectChanges();
        },
        error: (resp) => {
          this.isLoading = false;
          this.change.detectChanges();
        },
      });
    this.subs.push(sub);
  }

  getGlobalPortfolioReport(filterParams: any) {
    this.isLoading = true;
    const sub = this.reportService.getGlobalPortfolioReport(filterParams).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.reportUrl = response.data.statement;
        this.change.detectChanges();
      },
      error: (resp) => {
        this.isLoading = false;
        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  getActiveAndInactiveClientReport(status: string, filterParams: any) {
    this.isLoading = true;

    const sub = (
      this.export_format === 'pdf'
        ? this.reportService.getActiveAndInactiveClientPDFReport(status, filterParams)
        : this.reportService.getActiveAndInactiveClientCSVReport(status, filterParams)
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.reportUrl = response.data.statement;
        this.change.detectChanges();
      },
      error: (resp) => {
        this.isLoading = false;
        this.change.detectChanges();
      },
    });

    this.subs.push(sub);
  }

  getSubRedReport() {
    this.isLoading = true;
    const payload = {
      ...this.dateRangeForm.getRawValue(),
    };
    // If accountNumber exists, fetch the account ID first
    if (this.accountNumber) {
      const sub = this.accountService
        .getAccountDetails(this.accountNumber)
        .subscribe({
          next: (res) => {
            const accountId = res?.data?.[0]?.id;
            const finalPayload = {
              ...payload,
              account_id: accountId,
            };

            this.fetchSubRedReport(finalPayload);
          },
          error: () => {
            this.isLoading = false;

            this.change.detectChanges();
          },
        });

      this.subs.push(sub);
    } else {
      // No account number, fetch report directly
      this.fetchSubRedReport(payload);
    }
  }
  getDividendReport() {
    this.isLoading = true;

    // Build payload
    const payload = {
      ...this.dateRangeForm.getRawValue(),
    };

    if (this.accountNumber) {
      const sub = this.accountService
        .getAccountDetails(this.accountNumber)
        .subscribe({
          next: (res) => {
            const accountId = res?.data?.[0]?.id;
            const finalPayload = {
              ...payload,
              account_id: accountId,
            };

            this.fetchDividendReport(finalPayload);
          },
          error: () => {
            this.isLoading = false;

            this.change.detectChanges();
          },
        });

      this.subs.push(sub);
    } else {
      // No account number, fetch report directly
      this.fetchDividendReport(payload);
    }
  }

  getAccountRMReport() {
    this.isLoading = true;
    const sub = this.reportService.getAccountRMReport(this.RMId).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.reportUrl = response.data.presigned_url;
        this.change.detectChanges();
      },
      error: (resp) => {
        this.isLoading = false;
        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  private fetchSubRedReport(payload: MFDTransactions) {
    const sub = this.reportService.getMFDTransactionReport(payload).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.reportUrl = response.data.statement;
        this.change.detectChanges();
      },
      error: (resp) => {
        this.isLoading = false;
        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  private fetchDividendReport(payload: MFDTransactions) {
    // Choose the correct service based on accountId
    const service = this.accountNumber
      ? this.reportService.getIndividualDividendReport(payload)
      : this.reportService.getGeneralDividendReport(payload);

    console.log('payload', payload);

    const sub = service.subscribe({
      next: (response) => {
        this.isLoading = false;
        this.reportUrl = response.data.presigned_url;
        this.change.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.change.detectChanges();
      },
    });

    this.subs.push(sub);
  }

  openDocument(link: string) {
    window.open(link, '_blank');
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
